import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import { SportIcon } from '../components/SportIcon';

const SPORTS = [
  { id: 'pickleball', label: 'Pickleball' },
  { id: 'tennis', label: 'Tennis' },
  { id: 'badminton', label: 'Badminton' },
  { id: 'basketball', label: 'Basketball' },
  { id: 'volleyball', label: 'Volleyball' },
  { id: 'futsal', label: 'Futsal' },
  { id: 'other', label: 'Other' },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STEPS = ['Sport & Name', 'Schedule', 'Slot Settings', 'Pricing'];

// Normal times (open_time selector — 00:00 to 23:30)
const TIMES_NORMAL = (() => {
  const t = [];
  for (let h = 0; h < 24; h++) for (let m = 0; m < 60; m += 30)
    t.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
  return t;
})();

// Extended times including past-midnight for close_time and rate band ends
const TIMES_EXTENDED = (() => {
  const t = [...TIMES_NORMAL];
  for (let h = 24; h <= 27; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === 27 && m > 0) break;
      t.push(`${h}:${String(m).padStart(2,'0')}`);
    }
  }
  return t;
})();

// Display a time that might be past midnight
function displayTime(t) {
  if (!t) return '';
  const [hs, ms] = t.split(':');
  const h = parseInt(hs, 10); const m = parseInt(ms, 10);
  if (h >= 24) {
    const adjH = h - 24;
    const ampm = adjH >= 12 ? 'PM' : 'AM';
    const h12 = adjH % 12 || 12;
    return `${h12}:${String(m).padStart(2,'0')} ${ampm} (+1)`;
  }
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
}

function FieldLabel({ children }) {
  return <span className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>{children}</span>;
}

export default function SportsOpenCourtWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [sport, setSport] = useState('');
  const [openTime, setOpenTime] = useState('06:00');
  const [closeTime, setCloseTime] = useState('24:00');
  const [openDays, setOpenDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [slotMinutes, setSlotMinutes] = useState(30);
  const [minDuration, setMinDuration] = useState(30);
  // Always time-based pricing with 1–3+ bands
  const [rateBands, setRateBands] = useState([{ start: '06:00', end: '24:00', price: '' }]);
  const [paymentDisclaimer, setPaymentDisclaimer] = useState('');
  // Day-band schedules: group days (e.g. Mon–Wed / Thu–Fri / Sat–Sun), each with
  // its own hours + pricing. Off = one schedule for every open day (legacy).
  const [useGroups, setUseGroups] = useState(false);
  const [groups, setGroups] = useState([
    { days: [0, 1, 2, 3, 4, 5, 6], open: '06:00', close: '24:00', bands: [{ start: '06:00', end: '24:00', price: '' }] },
  ]);
  // Optional per-time-band minimum duration rules (same clock windows every day)
  const [minRules, setMinRules] = useState([]);

  // Load existing court for edit mode
  useEffect(() => {
    if (!isEdit) return;
    sportsApi.get('/sports/courts').then(r => {
      const c = (r.data || []).find(x => x.id === id);
      if (!c) { toast.error('Court not found'); navigate('/sports/courts'); return; }
      setName(c.name || '');
      setSport(c.sport || '');
      setOpenTime(c.open_time || '06:00');
      setCloseTime(c.close_time || '22:00');
      setOpenDays(c.open_days || [0, 1, 2, 3, 4, 5, 6]);
      setSlotMinutes(c.slot_minutes || 30);
      setMinDuration(c.min_duration_minutes || 30);
      setPaymentDisclaimer(c.payment_disclaimer || '');
      setMinRules((c.min_duration_rules || []).map(r => ({ start: r.start, end: r.end, min: String(r.min_minutes) })));
      if (c.schedules?.length) {
        setUseGroups(true);
        setGroups(c.schedules.map(s => ({
          days: s.days || [],
          open: s.open_time || c.open_time || '06:00',
          close: s.close_time || c.close_time || '24:00',
          bands: (s.rate_bands?.length ? s.rate_bands : (c.rate_bands || [])).map(b => ({ start: b.start, end: b.end, price: String(b.price) })),
        })));
      }
      // Convert flat-rate courts to a single time band for editing
      if (c.rate_type === 'flat') {
        setRateBands([{ start: c.open_time || '05:00', end: c.close_time || '24:00', price: String(c.flat_rate || '') }]);
      } else {
        setRateBands(c.rate_bands?.length
          ? c.rate_bands.map(b => ({ start: b.start, end: b.end, price: String(b.price) }))
          : [{ start: c.open_time || '05:00', end: c.close_time || '24:00', price: '' }]);
      }
    }).catch(() => { toast.error('Failed to load court'); navigate('/sports/courts'); });
  }, [id, isEdit, navigate]);

  const toggleDay = (i) => setOpenDays(prev => prev.includes(i) ? prev.filter(d => d !== i) : [...prev, i].sort());

  const addBand = () => setRateBands(prev => [...prev, { start: '06:00', end: '22:00', price: '' }]);
  const updateBand = (i, key, val) => setRateBands(prev => prev.map((b, idx) => idx === i ? { ...b, [key]: val } : b));
  const removeBand = (i) => setRateBands(prev => prev.filter((_, idx) => idx !== i));

  // ── Day-group helpers ──
  const addGroup = () => setGroups(prev => [...prev, { days: [], open: '06:00', close: '24:00', bands: [{ start: '06:00', end: '24:00', price: '' }] }]);
  const removeGroup = (gi) => setGroups(prev => prev.filter((_, idx) => idx !== gi));
  const updateGroup = (gi, key, val) => setGroups(prev => prev.map((g, idx) => idx === gi ? { ...g, [key]: val } : g));
  const toggleGroupDay = (gi, day) => setGroups(prev => prev.map((g, idx) => {
    if (idx !== gi) return g;
    return { ...g, days: g.days.includes(day) ? g.days.filter(d => d !== day) : [...g.days, day].sort() };
  }));
  const dayClaimedBy = (day) => groups.findIndex(g => g.days.includes(day));
  const updateGroupBand = (gi, bi, key, val) => setGroups(prev => prev.map((g, idx) => idx === gi
    ? { ...g, bands: g.bands.map((b, j) => j === bi ? { ...b, [key]: val } : b) } : g));
  const addGroupBand = (gi) => setGroups(prev => prev.map((g, idx) => idx === gi
    ? { ...g, bands: [...g.bands, { start: '06:00', end: '22:00', price: '' }] } : g));
  const removeGroupBand = (gi, bi) => setGroups(prev => prev.map((g, idx) => idx === gi
    ? { ...g, bands: g.bands.filter((_, j) => j !== bi) } : g));

  // ── Min-duration rule helpers ──
  const addMinRule = () => setMinRules(prev => [...prev, { start: '17:00', end: '24:00', min: '' }]);
  const updateMinRule = (i, key, val) => setMinRules(prev => prev.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const removeMinRule = (i) => setMinRules(prev => prev.filter((_, idx) => idx !== i));

  const validate = () => {
    if (step === 0) {
      if (!name.trim()) { toast.error('Court name is required'); return false; }
      if (!sport) { toast.error('Please select a sport'); return false; }
    }
    if (step === 1) {
      if (openDays.length === 0) { toast.error('Select at least one open day'); return false; }
      if (!useGroups) {
        if (openTime >= closeTime && closeTime !== '00:00') { toast.error('Close time must be after open time'); return false; }
      } else {
        for (let i = 0; i < groups.length; i++) {
          const g = groups[i];
          if (g.days.length === 0) { toast.error(`Schedule ${i + 1}: pick at least one day`); return false; }
          if (g.open >= g.close) { toast.error(`Schedule ${i + 1}: close time must be after open time`); return false; }
        }
        const claimed = groups.flatMap(g => g.days);
        if (new Set(claimed).size !== claimed.length) { toast.error('A day can only be in one schedule'); return false; }
      }
    }
    if (step === 2) {
      if (minDuration < slotMinutes) { toast.error('Min duration cannot be less than slot size'); return false; }
      for (const r of minRules) {
        if (!r.min || isNaN(Number(r.min)) || Number(r.min) < slotMinutes) {
          toast.error('Each minimum-duration rule needs minutes ≥ slot size'); return false;
        }
        if (r.start >= r.end) { toast.error('Minimum-duration rule: end must be after start'); return false; }
      }
    }
    if (step === 3) {
      if (!useGroups) {
        if (rateBands.some(b => !b.price || isNaN(Number(b.price)))) {
          toast.error('All rate bands need a valid price'); return false;
        }
      } else {
        for (let i = 0; i < groups.length; i++) {
          if (groups[i].bands.some(b => !b.price || isNaN(Number(b.price)))) {
            toast.error(`Schedule ${i + 1}: all rate bands need a valid price`); return false;
          }
        }
      }
    }
    return true;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    // With day-groups, court-level hours/bands act as the fallback for any
    // unclaimed day — use the first group's values so they're always sane.
    const fallback = useGroups && groups.length ? groups[0] : null;
    const payload = {
      name, sport,
      open_time: fallback ? fallback.open : openTime,
      close_time: fallback ? fallback.close : closeTime,
      open_days: openDays, slot_minutes: slotMinutes, min_duration_minutes: minDuration,
      rate_type: 'time_based',
      flat_rate: 0,
      rate_bands: (fallback ? fallback.bands : rateBands).map(b => ({ start: b.start, end: b.end, price: Number(b.price) })),
      payment_disclaimer: paymentDisclaimer,
      min_duration_rules: minRules.map(r => ({ start: r.start, end: r.end, min_minutes: Number(r.min) })),
      schedules: useGroups ? groups.map(g => ({
        days: g.days, open_time: g.open, close_time: g.close,
        rate_bands: g.bands.map(b => ({ start: b.start, end: b.end, price: Number(b.price) })),
      })) : [],
    };
    try {
      if (isEdit) await sportsApi.put(`/sports/courts/${id}`, payload);
      else await sportsApi.post('/sports/courts', payload);
      toast.success(isEdit ? 'Court updated!' : 'Court created!');
      navigate('/sports/courts');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save court');
    } finally { setSaving(false); }
  };

  const inputStyle = {
    background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff',
    borderRadius: 8, padding: '10px 14px', width: '100%', outline: 'none',
    fontFamily: 'Manrope, sans-serif', fontSize: 14,
  };
  const selectStyle = { ...inputStyle, appearance: 'none' };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />
      <main className="max-w-2xl mx-auto px-5 py-10">
        {/* Header */}
        <button onClick={() => navigate('/sports/courts')} className="flex items-center gap-1 text-sm mb-6 transition-colors"
          style={{ color: '#737373' }} onMouseEnter={e => e.currentTarget.style.color = '#fff'}
          onMouseLeave={e => e.currentTarget.style.color = '#737373'}>
          <ChevronLeft className="h-4 w-4" /> Back to My Courts
        </button>
        <h1 className="text-3xl uppercase tracking-tight mb-2" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>
          {isEdit ? 'Edit Court' : 'Open a Court'}
        </h1>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-8 mt-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <div className="flex items-center gap-1.5">
                <div className="h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                  style={{
                    background: i < step ? '#E63946' : i === step ? '#E6394620' : '#1E1E1E',
                    color: i < step ? '#fff' : i === step ? '#E63946' : '#555',
                    border: i === step ? '1px solid #E63946' : '1px solid transparent',
                  }}>
                  {i < step ? <Check className="h-3 w-3" /> : i + 1}
                </div>
                <span className="text-xs hidden sm:block" style={{ color: i === step ? '#fff' : '#555' }}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px mx-1" style={{ background: i < step ? '#E63946' : '#2A2A2A', minWidth: 16 }} />}
            </div>
          ))}
        </div>

        {/* Step 0: Sport & Name */}
        {step === 0 && (
          <div data-testid="sports-wizard-step-sport">
            <label className="block mb-5">
              <FieldLabel>Court Name</FieldLabel>
              <input value={name} onChange={e => setName(e.target.value)} data-testid="sports-wizard-name"
                placeholder="e.g. Court A" style={inputStyle} />
            </label>
            <FieldLabel>Sport</FieldLabel>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {SPORTS.map(s => (
                <button key={s.id} data-testid={`sports-wizard-sport-${s.id}`}
                  onClick={() => setSport(s.id)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all text-sm font-semibold"
                  style={{
                    background: sport === s.id ? '#E6394620' : '#141414',
                    border: `1px solid ${sport === s.id ? '#E63946' : '#2A2A2A'}`,
                    color: sport === s.id ? '#E63946' : '#A3A3A3',
                  }}>
                  <SportIcon sport={s.id} size={26} color={sport === s.id ? '#E63946' : '#555'} />
                  <span style={{ fontSize: 11 }}>{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Schedule */}
        {step === 1 && (
          <div data-testid="sports-wizard-step-schedule">
            <FieldLabel>Open Days</FieldLabel>
            <div className="flex gap-2 flex-wrap mb-5">
              {DAYS_SHORT.map((d, i) => (
                <button key={d} data-testid={`sports-wizard-day-${d}`}
                  onClick={() => toggleDay(i)}
                  className="px-3 py-1.5 rounded-md text-sm font-semibold transition-all"
                  style={{
                    background: openDays.includes(i) ? '#E63946' : '#1E1E1E',
                    color: openDays.includes(i) ? '#fff' : '#737373',
                  }}>{d}</button>
              ))}
            </div>

            {/* Same hours daily vs day-groups */}
            <FieldLabel>Hours</FieldLabel>
            <div className="flex gap-3 mb-5">
              {[{ v: false, label: 'Same every day' }, { v: true, label: 'Different by day group' }].map(o => (
                <button key={String(o.v)} onClick={() => setUseGroups(o.v)}
                  data-testid={`sports-wizard-hours-mode-${o.v ? 'groups' : 'same'}`}
                  className="flex-1 py-2.5 rounded-md text-sm font-semibold transition-all"
                  style={{
                    background: useGroups === o.v ? '#E63946' : '#1E1E1E',
                    color: useGroups === o.v ? '#fff' : '#737373',
                    border: `1px solid ${useGroups === o.v ? '#E63946' : '#2A2A2A'}`,
                  }}>{o.label}</button>
              ))}
            </div>

            {!useGroups && (
              <div className="grid grid-cols-2 gap-4">
                <label>
                  <FieldLabel>Open Time</FieldLabel>
                  <select value={openTime} onChange={e => setOpenTime(e.target.value)} style={selectStyle} data-testid="sports-wizard-open-time">
                    {TIMES_NORMAL.map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                  </select>
                </label>
                <label>
                  <FieldLabel>Close Time</FieldLabel>
                  <select value={closeTime} onChange={e => setCloseTime(e.target.value)} style={selectStyle} data-testid="sports-wizard-close-time">
                    {TIMES_EXTENDED.filter(t => t !== '00:00').map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                  </select>
                </label>
              </div>
            )}

            {useGroups && (
              <div className="space-y-3">
                {groups.map((g, gi) => (
                  <div key={gi} className="rounded-xl p-4" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }} data-testid={`sports-wizard-group-${gi}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#E63946' }}>Schedule {gi + 1}</span>
                      {groups.length > 1 && (
                        <button onClick={() => removeGroup(gi)} className="text-xs" style={{ color: '#FF2D55' }}>Remove</button>
                      )}
                    </div>
                    <div className="flex gap-1.5 flex-wrap mb-3">
                      {DAYS_SHORT.map((d, di) => {
                        const owner = dayClaimedBy(di);
                        const mine = g.days.includes(di);
                        const takenElsewhere = owner !== -1 && owner !== gi;
                        const closed = !openDays.includes(di);
                        return (
                          <button key={d} disabled={takenElsewhere || closed}
                            onClick={() => toggleGroupDay(gi, di)}
                            data-testid={`sports-wizard-group-${gi}-day-${d}`}
                            title={closed ? 'Court is closed this day' : takenElsewhere ? `In Schedule ${owner + 1}` : ''}
                            className="px-2.5 py-1 rounded text-xs font-semibold transition-all"
                            style={{
                              background: mine ? '#E63946' : '#1E1E1E',
                              color: mine ? '#fff' : (takenElsewhere || closed) ? '#3A3A3A' : '#737373',
                              cursor: (takenElsewhere || closed) ? 'not-allowed' : 'pointer',
                            }}>{d}</button>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs mb-1.5" style={{ color: '#555' }}>Open</p>
                        <select value={g.open} onChange={e => updateGroup(gi, 'open', e.target.value)}
                          style={{ ...selectStyle, padding: '8px 10px', fontSize: 13 }}>
                          {TIMES_NORMAL.map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                        </select>
                      </div>
                      <div>
                        <p className="text-xs mb-1.5" style={{ color: '#555' }}>Close</p>
                        <select value={g.close} onChange={e => updateGroup(gi, 'close', e.target.value)}
                          style={{ ...selectStyle, padding: '8px 10px', fontSize: 13 }}>
                          {TIMES_EXTENDED.filter(t => t !== '00:00').map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={addGroup} disabled={groups.length >= 4}
                  className="flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-md transition-colors"
                  style={{ color: '#E63946', background: '#E6394610', border: '1px solid #E6394630' }}
                  data-testid="sports-wizard-add-group">
                  + Add Day Group
                </button>
                <p className="text-xs" style={{ color: '#555' }}>
                  Example: Mon–Sat 5:00 AM – 3:00 AM (+1), Sun 12:00 PM – 3:00 AM (+1). Times marked (+1) run into the next day. Each schedule sets its own pricing in the Pricing step.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Slot Settings */}
        {step === 2 && (
          <div data-testid="sports-wizard-step-slots">
            <label className="block mb-5">
              <FieldLabel>Booking Slot Size</FieldLabel>
              <div className="flex gap-3">
                {[15, 30, 60].map(m => (
                  <button key={m} data-testid={`sports-wizard-slot-${m}`}
                    onClick={() => { setSlotMinutes(m); if (minDuration < m) setMinDuration(m); }}
                    className="flex-1 py-2.5 rounded-md text-sm font-semibold transition-all"
                    style={{
                      background: slotMinutes === m ? '#E63946' : '#1E1E1E',
                      color: slotMinutes === m ? '#fff' : '#737373',
                      border: `1px solid ${slotMinutes === m ? '#E63946' : '#2A2A2A'}`,
                    }}>{m} min</button>
                ))}
              </div>
            </label>
            <label className="block">
              <FieldLabel>Minimum Booking Duration (minutes)</FieldLabel>
              <input type="number" value={minDuration} onChange={e => setMinDuration(Number(e.target.value))}
                min={slotMinutes} step={slotMinutes} style={inputStyle} data-testid="sports-wizard-min-duration" />
              <p className="text-xs mt-1" style={{ color: '#555' }}>Customers must book at least this many minutes.</p>
            </label>

            {/* Per-time-band minimums, e.g. 30 min daytime but 2 hours from 5 PM */}
            <div className="mt-6">
              <FieldLabel>Different Minimums by Time (optional)</FieldLabel>
              <p className="text-xs mb-3" style={{ color: '#555' }}>
                Override the minimum for certain hours — e.g. evenings require 2-hour bookings. The rule that contains the booking's <strong style={{ color: '#A3A3A3' }}>start time</strong> applies.
              </p>
              <div className="space-y-2">
                {minRules.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end" data-testid={`sports-wizard-min-rule-${i}`}>
                    <div>
                      <p className="text-xs mb-1" style={{ color: '#555' }}>From</p>
                      <select value={r.start} onChange={e => updateMinRule(i, 'start', e.target.value)}
                        style={{ ...selectStyle, padding: '8px 10px', fontSize: 13 }}>
                        {TIMES_NORMAL.map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                      </select>
                    </div>
                    <div>
                      <p className="text-xs mb-1" style={{ color: '#555' }}>To</p>
                      <select value={r.end} onChange={e => updateMinRule(i, 'end', e.target.value)}
                        style={{ ...selectStyle, padding: '8px 10px', fontSize: 13 }}>
                        {TIMES_EXTENDED.filter(t => t !== '00:00').map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                      </select>
                    </div>
                    <div>
                      <p className="text-xs mb-1" style={{ color: '#555' }}>Min (minutes)</p>
                      <input type="number" value={r.min} min={slotMinutes} step={slotMinutes} placeholder="120"
                        onChange={e => updateMinRule(i, 'min', e.target.value)}
                        style={{ ...inputStyle, padding: '8px 10px', fontSize: 13 }} />
                    </div>
                    <button onClick={() => removeMinRule(i)} className="text-xs pb-2.5" style={{ color: '#FF2D55' }}>✕</button>
                  </div>
                ))}
              </div>
              <button onClick={addMinRule} disabled={minRules.length >= 4}
                className="mt-2 flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-md transition-colors"
                style={{ color: '#E63946', background: '#E6394610', border: '1px solid #E6394630' }}
                data-testid="sports-wizard-add-min-rule">
                + Add Time Rule
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Pricing */}
        {step === 3 && (
          <div data-testid="sports-wizard-step-pricing">
            <p className="text-sm mb-5" style={{ color: '#A3A3A3', lineHeight: 1.6 }}>
              Set different rates by time of day. Add 2–3 bands for morning, midday, and evening. All bands are <strong style={{ color: '#fff' }}>per hour</strong> (₱/hr) — the system computes the exact amount based on session length.
            </p>
            {useGroups && (
              <div className="space-y-5">
                {groups.map((g, gi) => (
                  <div key={gi} className="rounded-xl p-4" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
                    <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#E63946' }}>
                      Schedule {gi + 1} — {g.days.map(d => DAYS_SHORT[d]).join(', ') || 'no days'}
                    </p>
                    <p className="text-xs mb-3" style={{ color: '#555' }}>{displayTime(g.open)} – {displayTime(g.close)}</p>
                    <div className="space-y-2">
                      {g.bands.map((b, bi) => (
                        <div key={bi} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                          <div>
                            <p className="text-xs mb-1" style={{ color: '#555' }}>From</p>
                            <select value={b.start} onChange={e => updateGroupBand(gi, bi, 'start', e.target.value)}
                              style={{ ...selectStyle, padding: '8px 10px', fontSize: 13 }}>
                              {TIMES_NORMAL.map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                            </select>
                          </div>
                          <div>
                            <p className="text-xs mb-1" style={{ color: '#555' }}>To</p>
                            <select value={b.end} onChange={e => updateGroupBand(gi, bi, 'end', e.target.value)}
                              style={{ ...selectStyle, padding: '8px 10px', fontSize: 13 }}>
                              {TIMES_EXTENDED.filter(t => t !== '00:00').map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                            </select>
                          </div>
                          <div>
                            <p className="text-xs mb-1" style={{ color: '#555' }}>₱ / hr</p>
                            <input type="number" value={b.price} placeholder="150"
                              onChange={e => updateGroupBand(gi, bi, 'price', e.target.value)}
                              style={{ ...inputStyle, padding: '8px 10px', fontSize: 13 }} />
                          </div>
                          {g.bands.length > 1
                            ? <button onClick={() => removeGroupBand(gi, bi)} className="text-xs pb-2.5" style={{ color: '#FF2D55' }}>✕</button>
                            : <span />}
                        </div>
                      ))}
                    </div>
                    <button onClick={() => addGroupBand(gi)} disabled={g.bands.length >= 6}
                      className="mt-2 text-sm font-semibold px-3 py-1 rounded-md"
                      style={{ color: '#E63946', background: '#E6394610', border: '1px solid #E6394630' }}>
                      + Add Band
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!useGroups && (<>
            <FieldLabel>Rate Bands (₱ per hour)</FieldLabel>
            <div className="space-y-3">
              {rateBands.map((b, i) => (
                <div key={i} className="rounded-xl p-4" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#E63946' }}>Band {i + 1}</span>
                    {rateBands.length > 1 && (
                      <button onClick={() => removeBand(i)} className="text-xs" style={{ color: '#FF2D55' }}>Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <p className="text-xs mb-1.5" style={{ color: '#555' }}>From</p>
                      <select value={b.start} onChange={e => updateBand(i, 'start', e.target.value)}
                        style={{ ...selectStyle, padding: '8px 10px', fontSize: 13 }}>
                        {TIMES_NORMAL.map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                      </select>
                    </div>
                    <div>
                      <p className="text-xs mb-1.5" style={{ color: '#555' }}>To</p>
                      <select value={b.end} onChange={e => updateBand(i, 'end', e.target.value)}
                        style={{ ...selectStyle, padding: '8px 10px', fontSize: 13 }}>
                        {TIMES_EXTENDED.filter(t => t !== '00:00').map(t => <option key={t} value={t}>{displayTime(t)}</option>)}
                      </select>
                    </div>
                    <div>
                      <p className="text-xs mb-1.5" style={{ color: '#555' }}>₱ / hr</p>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#737373' }}>₱</span>
                        <input type="number" value={b.price} onChange={e => updateBand(i, 'price', e.target.value)}
                          placeholder="150"
                          style={{ ...inputStyle, padding: '8px 10px 8px 22px', fontSize: 13 }} />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs mt-2" style={{ color: '#555' }}>
                    {b.start && b.end && b.price ? `${displayTime(b.start)} – ${displayTime(b.end)}: ₱${Number(b.price).toLocaleString()}/hr` : 'Set times and rate above'}
                  </p>
                </div>
              ))}
            </div>
            <button onClick={addBand} disabled={rateBands.length >= 6}
              className="mt-3 flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-md transition-colors"
              style={{ color: '#E63946', background: '#E6394610', border: '1px solid #E6394630' }}
              data-testid="sports-wizard-add-band">
              + Add Time Band
            </button>
            </>)}
            <div className="mt-4 rounded-lg p-3" style={{ background: '#0A1A0A', border: '1px solid #0A2A10' }}>
              <p className="text-xs" style={{ color: '#5A9A6A' }}>
                Tip: A booking crossing multiple bands (e.g., 11am–1pm crossing Morning → Midday) is automatically split and priced correctly.
              </p>
            </div>

            <div className="mt-5">
              <FieldLabel>Payment & Refund Policy (shown before payment)</FieldLabel>
              <textarea value={paymentDisclaimer} onChange={e => setPaymentDisclaimer(e.target.value)}
                data-testid="sports-wizard-payment-disclaimer" rows={4} maxLength={600}
                placeholder="e.g. No cancellations on the day of booking. Refunds only for cancellations due to rainy weather (this is an open court)."
                style={{ width: '100%', background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '10px 12px', outline: 'none', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'Manrope, sans-serif' }} />
              <p className="text-xs mt-1" style={{ color: '#555' }}>Set per court — e.g. roofed courts may allow rainy-day play while open courts offer weather refunds. Leave blank to show nothing.</p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6" style={{ borderTop: '1px solid #1E1E1E' }}>
          <button onClick={step === 0 ? () => navigate('/sports/courts') : back}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors"
            style={{ background: '#1E1E1E', color: '#A3A3A3' }}
            data-testid="sports-wizard-back-btn">
            <ChevronLeft className="h-4 w-4" /> {step === 0 ? 'Cancel' : 'Back'}
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} data-testid="sports-wizard-next-btn"
              className="flex items-center gap-2 px-5 py-2 rounded-md text-sm font-bold uppercase tracking-wide transition-all"
              style={{ background: '#E63946', color: '#fff' }}>
              Next <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={save} disabled={saving} data-testid="sports-wizard-save-btn"
              className="flex items-center gap-2 px-5 py-2 rounded-md text-sm font-bold uppercase tracking-wide transition-all"
              style={{ background: '#E63946', color: '#fff', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Court'}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
